import { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Search } from 'lucide-react';
import { cn } from '@/utils/cn';
import type {
  AlarmEquipmentChip,
  AlarmFilterClusterVariant,
  AlarmFilterValue,
  AlarmRowStatus,
  AlarmSeverity,
  AlarmTimeRangeKey,
} from './types';
import { DEFAULT_FILTER_VALUE, STATUS_LABELS } from './types';

// Re-export under the consumer-facing names kept stable for callers.
export type FilterValue = AlarmFilterValue;
export type TimeRangeKey = AlarmTimeRangeKey;

interface Props {
  value: FilterValue;
  onChange: (next: FilterValue) => void;
  variant?: AlarmFilterClusterVariant;
  /** When true (default), reads/writes URL search params. When false, fully controlled. */
  persistInUrl?: boolean;
  /** Equipment options to render. Defaults to spec §6.2 list. */
  equipmentOptions?: AlarmEquipmentChip[];
  /**
   * When false, hides the Time chips section entirely. Defaults to true.
   * Set to false on the CustomerAlarms page for non-History tabs — time
   * filtering must never apply to Needs Attention / Acknowledged buckets.
   */
  showTimeFilter?: boolean;
  /** Internal-use override for tests; defaults to 250ms. */
  searchDebounceMs?: number;
  className?: string;
}

const SEVERITIES: AlarmSeverity[] = ['critical', 'warning', 'info'];
const STATUSES: AlarmRowStatus[] = ['needs_attention', 'acknowledged', 'history'];
const TIME_RANGES: TimeRangeKey[] = ['1h', '4h', '24h', '7d', '30d'];
const DEFAULT_EQUIPMENT: AlarmEquipmentChip[] = ['BMS', 'PCS', 'generator', 'meter', 'pv', 'ev'];

export function AlarmFilterCluster({
  value,
  onChange,
  variant = 'expanded',
  persistInUrl = true,
  equipmentOptions = DEFAULT_EQUIPMENT,
  showTimeFilter = true,
  searchDebounceMs = 250,
  className,
}: Props) {
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchInput, setSearchInput] = useState(value.search);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // The filter state that `value` and the URL currently agree on, in canonical
  // serialized form. BOTH sync effects below compare against this shared
  // baseline (not against each other) — that is what breaks the value↔URL
  // ping-pong: after either direction syncs, the baseline equals both sides, so
  // neither effect re-fires. Initialized from the mount value so the value→URL
  // effect cannot clobber a deep-linked URL on first render (the "Initial
  // URL→state→URL race", LOW 1 in the PR #238 review).
  const syncedRef = useRef<string>();
  if (syncedRef.current === undefined) {
    syncedRef.current = serializeFilter(value);
  }

  // value → URL. Fires only when the local value diverges from the agreed
  // baseline (a user toggled a chip / typed a search) — never as an echo of a
  // URL→value sync, because that path advances the baseline first. replace:false
  // so each genuine filter change pushes a history entry: that is what makes
  // browser back/forward through filter states work (#239).
  // mergeFilterIntoSearchParams preserves unrelated keys (e.g. the `alarm`
  // detail-panel deep-link, #232).
  useEffect(() => {
    if (!persistInUrl) return;
    if (serializeFilter(value) === syncedRef.current) return;
    syncedRef.current = serializeFilter(value);
    const next = mergeFilterIntoSearchParams(searchParams, value);
    if (next.toString() !== searchParams.toString()) {
      setSearchParams(next, { replace: false });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, persistInUrl]);

  // URL → value. Handles the initial mount read AND external navigation
  // (browser back/forward) — react-router updates `searchParams` on both. Fires
  // onChange only when the URL's filter portion diverges from the agreed
  // baseline, so it ignores the echo of our own writes and `alarm`-only changes.
  // A URL with no filter keys resolves to the default filter (so back-navigating
  // to the bare path restores defaults rather than freezing the last selection).
  useEffect(() => {
    if (!persistInUrl) return;
    const parsed = parseFromSearchParams(searchParams) ?? DEFAULT_FILTER_VALUE;
    if (serializeFilter(parsed) === syncedRef.current) return;
    syncedRef.current = serializeFilter(parsed);
    setSearchInput(parsed.search);
    onChange(parsed);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, persistInUrl]);

  // Keep search input in sync with external value changes.
  useEffect(() => {
    setSearchInput(value.search);
  }, [value.search]);

  const toggleArr = <T,>(arr: readonly T[], item: T): T[] =>
    arr.includes(item) ? arr.filter((x) => x !== item) : [...arr, item];

  const handleSearchChange = (next: string) => {
    setSearchInput(next);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      onChange({ ...value, search: next });
    }, searchDebounceMs);
  };

  const showAdvanced = variant === 'expanded';

  return (
    <div
      role="toolbar"
      aria-label="Alarm filters"
      data-variant={variant}
      className={cn(
        'flex flex-wrap gap-2 p-3 border border-ems-border rounded bg-ems-bg-card text-xs items-center',
        className,
      )}
    >
      <ChipGroupLabel label="Severity" />
      {SEVERITIES.map((s) => (
        <Chip
          key={s}
          label={cap(s)}
          active={value.severity.includes(s)}
          onClick={() => onChange({ ...value, severity: toggleArr(value.severity, s) })}
          data-chip={`severity-${s}`}
        />
      ))}
      <Sep />
      <ChipGroupLabel label="Status" />
      {STATUSES.map((s) => (
        <Chip
          key={s}
          label={STATUS_LABELS[s]}
          active={value.status.includes(s)}
          onClick={() => onChange({ ...value, status: toggleArr(value.status, s) })}
          data-chip={`status-${s}`}
        />
      ))}

      {showAdvanced && (
        <>
          <Sep />
          <ChipGroupLabel label="Equipment" />
          {equipmentOptions.map((e) => (
            <Chip
              key={e}
              label={e}
              active={value.equipment.includes(e)}
              onClick={() => onChange({ ...value, equipment: toggleArr(value.equipment, e) })}
              data-chip={`equipment-${e}`}
            />
          ))}
          {showTimeFilter && (
            <>
              <Sep />
              <ChipGroupLabel label="Time" />
              {TIME_RANGES.map((t) => (
                <Chip
                  key={t}
                  label={t}
                  active={value.timeRange === t}
                  onClick={() => onChange({ ...value, timeRange: t })}
                  data-chip={`time-${t}`}
                />
              ))}
            </>
          )}
          <Sep />
          <label className="flex items-center gap-1 ml-auto">
            <Search className="w-3 h-3 text-ems-text-muted" aria-hidden="true" />
            <input
              type="search"
              value={searchInput}
              onChange={(e) => handleSearchChange(e.target.value)}
              placeholder="Search alarms..."
              aria-label="Search alarms"
              className="bg-ems-bg border border-ems-border rounded px-2 py-1 text-xs text-ems-text-primary placeholder:text-ems-text-muted focus:outline-none focus:ring-2 focus:ring-ems-blue/40 w-40"
            />
          </label>
        </>
      )}
    </div>
  );
}

interface ChipProps {
  label: string;
  active: boolean;
  onClick: () => void;
  ['data-chip']?: string;
}

function Chip({ label, active, onClick, ...rest }: ChipProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={cn(
        'px-3 py-1 rounded-full border text-xs transition-colors',
        active
          ? 'bg-ems-blue/15 border-ems-blue text-ems-blue'
          : 'border-ems-border text-ems-text-secondary hover:text-ems-text-primary hover:border-ems-border-bright',
      )}
      {...rest}
    >
      {label}
    </button>
  );
}

function ChipGroupLabel({ label }: { label: string }) {
  return (
    <span className="text-[10px] uppercase tracking-wider text-ems-text-muted">{label}</span>
  );
}

function Sep() {
  return (
    <span className="opacity-30 mx-1" aria-hidden="true">
      ·
    </span>
  );
}

function cap(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1);
}

function parseFromSearchParams(sp: URLSearchParams): FilterValue | null {
  const has =
    sp.has('sev') ||
    sp.has('status') ||
    sp.has('equipment') ||
    sp.has('time') ||
    sp.has('q');
  if (!has) return null;
  const sev = (sp.get('sev')?.split(',').filter(Boolean) ?? []).filter(isSeverity);
  const status = normalizeStatusTokens(sp.get('status')?.split(',').filter(Boolean) ?? []);
  const equipment = (sp.get('equipment')?.split(',').filter(Boolean) ?? []).filter(isEquipment);
  const timeRange = parseTime(sp.get('time'));
  const search = sp.get('q') ?? '';
  return {
    severity: sev,
    // Default status mirrors DEFAULT_FILTER_VALUE — needs_attention only (#433).
    status: status.length ? status : ['needs_attention'],
    equipment,
    timeRange,
    search,
  };
}

const FILTER_KEYS = ['sev', 'status', 'equipment', 'time', 'q'] as const;

/**
 * Merge filter state into existing URLSearchParams without disturbing unrelated
 * keys (e.g. `alarm` deep-link param). Filter keys are removed when their value
 * matches the default, then re-set when present.
 */
function mergeFilterIntoSearchParams(
  current: URLSearchParams,
  v: FilterValue,
): URLSearchParams {
  const next = new URLSearchParams(current);
  for (const k of FILTER_KEYS) next.delete(k);
  if (v.severity.length) next.set('sev', v.severity.join(','));
  // Omit status from the URL when it equals the default (needs_attention only, #433).
  const isDefaultStatus =
    v.status.length === 1 && v.status[0] === 'needs_attention';
  if (v.status.length && !isDefaultStatus) next.set('status', v.status.join(','));
  if (v.equipment.length) next.set('equipment', v.equipment.join(','));
  if (v.timeRange !== '24h') next.set('time', v.timeRange);
  if (v.search) next.set('q', v.search);
  return next;
}

/**
 * Canonical filter-only serialization, used as the value↔URL sync baseline.
 * Reuses mergeFilterIntoSearchParams against an empty param set so it applies the
 * exact same default-omission rules as the URL writer — a filter and the URL it
 * produces serialize identically, which is what makes the sync reach a fixpoint
 * (no feedback loop). Unrelated keys (e.g. `alarm`) are excluded by construction.
 */
function serializeFilter(v: FilterValue): string {
  return mergeFilterIntoSearchParams(new URLSearchParams(), v).toString();
}

function isSeverity(s: string): s is AlarmSeverity {
  return s === 'critical' || s === 'warning' || s === 'info';
}

function isStatus(s: string): s is AlarmRowStatus {
  return s === 'needs_attention' || s === 'acknowledged' || s === 'history';
}

/**
 * Map raw ?status= tokens to current AlarmRowStatus values, accepting the
 * pre-rename vocabulary so existing deep-links don't break:
 *   active  -> [needs_attention, acknowledged]   (old superset)
 *   acked   -> acknowledged
 *   cleared -> history
 * Already-current tokens pass through; unknown tokens are dropped.
 */
function normalizeStatusTokens(tokens: string[]): AlarmRowStatus[] {
  const out = new Set<AlarmRowStatus>();
  for (const t of tokens) {
    if (isStatus(t)) out.add(t);
    else if (t === 'active') { out.add('needs_attention'); out.add('acknowledged'); }
    else if (t === 'acked') out.add('acknowledged');
    else if (t === 'cleared') out.add('history');
  }
  return [...out];
}

function isEquipment(s: string): s is AlarmEquipmentChip {
  return ['BMS', 'PCS', 'generator', 'meter', 'pv', 'ev'].includes(s);
}

function parseTime(raw: string | null): TimeRangeKey {
  if (raw === '1h' || raw === '4h' || raw === '24h' || raw === '7d' || raw === '30d') return raw;
  return '24h';
}
